import { baseApi } from '../base.js'
import { with2FAHeader } from '../utils/with2FAHeader.js'
import { moveFileOptimisticUpdate } from './utils.js'

export const requestTaskApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getRequestTask: build.query({
      query: requestTaskId =>
        with2FAHeader({
          url: `request-task/${requestTaskId}`,
        }),
      providesTags: (result, error, requestTaskId) => [
        { type: 'RequestTask', id: requestTaskId },
      ],
    }),

    matchFilesToDocuments: build.mutation({
      query: ({ requestTaskId, files, firmId }) =>
        with2FAHeader({
          url: 'request-task/match-to-documents',
          method: 'PUT',
          body: { requestTaskId, files, firmId },
        }),
      invalidatesTags: (result, error, { requestTaskId }) => [
        { type: 'RequestTask', id: requestTaskId },
      ],
    }),

    moveFileToDocument: build.mutation({
      query: ({
        requestTaskId,
        fileId,
        fromDocumentId,
        toDocumentId,
        firmId,
      }) =>
        with2FAHeader({
          url: 'request-task/move-file-to-document',
          method: 'PUT',
          body: { requestTaskId, fileId, fromDocumentId, toDocumentId, firmId },
        }),
      onQueryStarted: moveFileOptimisticUpdate,
    }),

    moveFileToDocumentWithCompletion: build.mutation({
      query: ({
        requestTaskId,
        fileId,
        fromDocumentId,
        toDocumentId,
        firmId,
      }) =>
        with2FAHeader({
          url: 'request-task/move-file-to-document-with-completion',
          method: 'PUT',
          body: { requestTaskId, fileId, fromDocumentId, toDocumentId, firmId },
        }),
      onQueryStarted: moveFileOptimisticUpdate,
    }),

    removeFile: build.mutation({
      query: ({ firmId, requestTaskId, documentId, fileId }) =>
        with2FAHeader({
          url: 'request-task/remove-file',
          method: 'POST',
          body: { firmId, requestTaskId, documentId, fileId },
        }),
      onQueryStarted: async (
        { requestTaskId, documentId, fileId },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistic update
        const patchResult = dispatch(
          requestTaskApi.util.updateQueryData(
            'getRequestTask',
            requestTaskId,
            draft => {
              const { requestTask } = draft
              if (!requestTask) {
                return
              }

              if (!documentId) {
                // Remove from unmatched files
                if (requestTask.unmatched_files) {
                  requestTask.unmatched_files =
                    requestTask.unmatched_files.filter(
                      f => f._id !== parseInt(fileId),
                    )
                }
              } else {
                // Remove from document
                const doc = requestTask.documents?.find(
                  d => d.id === documentId,
                )
                if (doc?.files) {
                  doc.files = doc.files.filter(f => f._id !== parseInt(fileId))
                }
              }
            },
          ),
        )

        try {
          await queryFulfilled
        } catch {
          // Revert optimistic update on error
          patchResult.undo()
        }
      },
    }),

    checkFolderIsRequestTask: build.query({
      query: id =>
        with2FAHeader({
          url: `request-task/folder/${id}/is-request-task`,
        }),
      providesTags: (result, error, id) => [
        { type: 'RequestTaskFolder', id: id },
      ],
    }),

    updateDocumentStatus: build.mutation({
      query: ({ requestTaskId, documentId, status, firmId }) =>
        with2FAHeader({
          url: `request-task/document/${documentId}/status`,
          method: 'PUT',
          body: { requestTaskId, status, firmId },
        }),
      onQueryStarted: async (
        { requestTaskId, documentId, status },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          requestTaskApi.util.updateQueryData(
            'getRequestTask',
            requestTaskId,
            draft => {
              const { requestTask } = draft
              if (!requestTask) {
                return
              }

              const doc = requestTask.documents?.find(
                d => String(d.id) === String(documentId),
              )
              if (doc) {
                doc.status = status
              }
            },
          ),
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    openRequestTask: build.mutation({
      query: ({ requestTaskId, firmId }) =>
        with2FAHeader({
          url: `request-task/${requestTaskId}/open`,
          method: 'PUT',
          body: { firmId },
        }),

      onQueryStarted: async (
        { requestTaskId },
        { dispatch, queryFulfilled },
      ) => {
        // optimistic update
        const patchResult = dispatch(
          requestTaskApi.util.updateQueryData(
            'getRequestTask',
            requestTaskId,
            draft => {
              const { requestTask } = draft
              if (!requestTask) {
                return
              }

              // ONLY optimistic change
              requestTask.status = 'rejected'
            },
          ),
        )

        try {
          const { data } = await queryFulfilled

          // sync with server response if it returns the task
          if (data?.requestTask) {
            dispatch(
              requestTaskApi.util.updateQueryData(
                'getRequestTask',
                requestTaskId,
                draft => {
                  Object.assign(draft, data.requestTask)
                },
              ),
            )
          }
        } catch {
          // rollback if backend rejects
          patchResult.undo()
        }
      },

      invalidatesTags: (result, error, { requestTaskId }) => [
        { type: 'RequestTask', id: requestTaskId },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetRequestTaskQuery,
  useMatchFilesToDocumentsMutation,
  useMoveFileToDocumentMutation,
  useMoveFileToDocumentWithCompletionMutation,
  useRemoveFileMutation,
  useUpdateDocumentStatusMutation,
  useOpenRequestTaskMutation,
} = requestTaskApi
